import { useEffect, useMemo, useState } from 'react'
import { calendarDateToIso } from '@utils/dashboardFinance.util'
import { useDashboardRefreshKey } from '@context/DashboardRefreshContext'
import {
  useDashboardClinicBootstrapPending,
  useDashboardClinicId,
} from './useDashboardPeriod'

export type HuddleDateQueryParams = {
  clinicId: string
  date: string
}

export type HuddleDateFetcher<T> = (params: HuddleDateQueryParams) => Promise<T>

export type UseHuddleDateFetchOptions = {
  /** Defaults to calendarDateToIso. */
  toQueryDate?: (date: string) => string
  errorMessage?: string
  /** Skip the request when false (hidden section or a later fetch wave). */
  enabled?: boolean
  /** Visible section waiting for its wave. Shows loading without fetching. */
  queued?: boolean
}

export type HuddleDateFetchState<T> = {
  data: T | null
  loading: boolean
  error: string | null
}

/**
 * Shared clinic+date fetch effect used by Huddle Overview/Detail hook pairs.
 */
export function useHuddleTableScope(
  date: string,
): HuddleDateQueryParams | null {
  const clinicId = useDashboardClinicId()
  return useMemo(() => {
    if (!clinicId || !date) return null
    return { clinicId, date: calendarDateToIso(date) }
  }, [clinicId, date])
}

export function useHuddleDateFetch<T>(
  date: string,
  fetcher: HuddleDateFetcher<T>,
  options?: UseHuddleDateFetchOptions,
): HuddleDateFetchState<T> {
  const clinicId = useDashboardClinicId()
  const clinicBootstrapPending = useDashboardClinicBootstrapPending()
  const refreshKey = useDashboardRefreshKey()
  const toQueryDate = options?.toQueryDate ?? calendarDateToIso
  const errorMessage = options?.errorMessage ?? 'Failed to load huddle data'
  const enabled = options?.enabled !== false
  const queued = options?.queued === true && !enabled
  const [data, setData] = useState<T | null>(null)
  const [loading, setLoading] = useState(
    () =>
      queued ||
      (enabled && (Boolean(clinicId && date) || clinicBootstrapPending)),
  )
  const [error, setError] = useState<string | null>(null)

  const queryParams = useMemo(() => {
    if (!enabled || !clinicId || !date) return null
    return {
      clinicId,
      date: toQueryDate(date),
    }
  }, [enabled, clinicId, date, toQueryDate])

  useEffect(() => {
    if (!queryParams) {
      setData(null)
      if (queued) {
        setError(null)
        setLoading(true)
      } else if (!enabled) {
        setError(null)
        setLoading(false)
      } else if (clinicBootstrapPending) {
        setLoading(true)
        setError(null)
      } else if (!clinicId) {
        setError('No clinic selected')
        setLoading(false)
      } else {
        setError(null)
        setLoading(false)
      }
      return
    }

    let cancelled = false
    setLoading(true)
    setError(null)

    fetcher(queryParams)
      .then((result) => {
        if (cancelled) return
        setData(result)
      })
      .catch((e: unknown) => {
        if (cancelled) return
        setError(e instanceof Error ? e.message : errorMessage)
        setData(null)
      })
      .finally(() => {
        if (!cancelled) setLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [
    queryParams,
    clinicId,
    clinicBootstrapPending,
    refreshKey,
    fetcher,
    errorMessage,
    enabled,
    queued,
  ])

  return { data, loading: loading || queued, error }
}
